import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, ChevronRight, ClipboardList, Flame, Gavel, MailWarning, Inbox, AlarmClock, CalendarCheck, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { OutcomeSheet } from "./OutcomeSheet";
import { SourceBadge } from "./SourceBadge";
import { LoadError, errMsg } from "./LoadError";
import { gbp, ukDate } from "./crmPipelineShared";
import { PushToggle, isStandalone } from "./PushToggle";

const COUNTERS = [
  ["new", "New enquiries", Inbox, "text-blue-300"],
  ["due_today", "Due today", CalendarClock, "text-brass"],
  ["overdue", "Overdue", AlarmClock, "text-red-300"],
  ["appointments_today", "Appointments", CalendarCheck, "text-emerald-300"],
];
const KIND_FOR = { new: "new", due_today: "due_today", overdue: "overdue", appointments_today: "appointment" };
const KIND_TONE = { reply: "bg-sky-500/20 text-sky-200", new: "bg-blue-500/15 text-blue-200", overdue: "bg-red-500/15 text-red-200", appointment: "bg-emerald-500/15 text-emerald-200",
  due_today: "bg-brass/15 text-brass", hot: "bg-orange-500/15 text-orange-200", referral: "bg-violet-500/15 text-violet-200" };
const iso = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

function QueueRow({ it, onLog, onBump }) {
  const key = `${it.kind}-${it.store}-${it.id}-${it.referral_id || ""}`;
  return (
    <li className="bg-[#0F2E23] border border-white/10 rounded-xl p-3.5" data-testid={`today-item-${key}`}>
      <div className="flex items-start gap-3">
        <Link to={it.link} className="min-w-0 flex-1" data-testid={`today-open-${key}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-white font-medium truncate">{it.name}</span>
            {it.hot && <Flame className="w-3.5 h-3.5 text-orange-300" />}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${KIND_TONE[it.kind]}`}>{it.label}</span>
            {it.source && <SourceBadge source={it.source} />}
            {it.pipeline_stage && <span className="text-[11px] text-emerald-100/50">{it.pipeline_stage}</span>}
          </div>
          {(it.property_address || it.next_follow_up) && <div className="text-[11px] text-emerald-100/40 mt-1">{it.property_address}{it.property_address && it.next_follow_up ? " · " : ""}{it.next_follow_up ? `Follow-up ${ukDate(it.next_follow_up)}` : ""}</div>}
        </Link>
        <ChevronRight className="w-4 h-4 text-emerald-100/30 mt-1 shrink-0" />
      </div>
      <div className="flex gap-2 mt-3">
        {it.mobile && <a href={`tel:${it.mobile}`} className="flex items-center justify-center gap-1.5 bg-brass text-emerald-950 font-semibold rounded-lg h-10 px-4 text-sm" data-testid={`today-call-${key}`}><Phone className="w-4 h-4" />Call</a>}
        {it.kind !== "referral" && <button onClick={() => onLog(it)} className="flex-1 flex items-center justify-center gap-1.5 bg-white/[0.06] hover:bg-white/10 rounded-lg h-10 text-sm text-white" data-testid={`today-log-${key}`}><ClipboardList className="w-4 h-4" />Log outcome</button>}
        {it.kind !== "referral" && it.kind !== "new" && [["Tmrw", 1], ["+3d", 3]].map(([l, n]) => (
          <button key={l} onClick={() => onBump(it, n)} className="bg-white/[0.06] hover:bg-white/10 rounded-lg h-10 px-3 text-xs text-emerald-100/80" data-testid={`today-bump-${n}-${key}`}>{l}</button>
        ))}
        {it.kind === "referral" && <Link to={it.link} className="flex-1 flex items-center justify-center gap-1.5 bg-white/[0.06] rounded-lg h-10 text-sm text-white" data-testid={`today-referral-open-${key}`}><Gavel className="w-4 h-4" />Update referral</Link>}
      </div>
    </li>
  );
}

function AuctionStrip({ a }) {
  const cells = [["Active", a.active], ["Instructed+", a.instructed], ["Expected fees", gbp(a.expected_fees)], ["Earned, unpaid", gbp(a.earned_unpaid)], ["Received", gbp(a.received)]];
  return (
    <Link to="/crm/referral-dashboard" className="block bg-[#0F2E23] border border-white/10 rounded-xl p-4 hover:border-brass/40 transition-colors" data-testid="today-auction-strip">
      <div className="flex items-center gap-2 mb-3"><Gavel className="w-4 h-4 text-brass" /><span className="text-sm font-semibold text-white">Auction instructions & fees</span><ChevronRight className="w-4 h-4 text-emerald-100/30 ml-auto" /></div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">{cells.map(([l, v]) => <div key={l}><div className="text-[11px] text-emerald-100/50">{l}</div><div className="text-white font-mono text-sm" data-testid={`auction-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</div></div>)}</div>
      {Object.keys(a.by_stage || {}).length > 0 && <div className="flex flex-wrap gap-1.5 mt-3">{Object.entries(a.by_stage).map(([s, n]) => <span key={s} className="text-[11px] bg-white/5 rounded-full px-2 py-0.5 text-emerald-100/70">{s}: {n}</span>)}</div>}
    </Link>
  );
}

export default function CrmToday() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("");
  const [target, setTarget] = useState(null);

  const load = useCallback(() => {
    setErr("");
    api.get("/crm/today").then((r) => setData(r.data)).catch((e) => setErr(errMsg(e, "Could not load today's desk")));
  }, []);
  useEffect(load, [load]);
  useEffect(() => { api.post("/crm/origin", { origin: window.location.origin }).catch(() => {}); }, []);

  const bump = async (it, n) => {
    try { await api.post(`/crm/unified-contacts/${it.store}/${it.id}/followup`, { date: iso(n) }); toast.success(`Moved to ${ukDate(iso(n))}`); load(); }
    catch (e) { toast.error(errMsg(e, "Could not reschedule")); }
  };

  if (err && !data) return <CrmShell title="Today"><LoadError message={err} onRetry={load} testId="today-error" /></CrmShell>;
  if (!data) return <CrmShell title="Today"><div className="text-emerald-100/50 text-sm py-10 text-center">Loading…</div></CrmShell>;
  const queue = filter ? data.queue.filter((i) => i.kind === KIND_FOR[filter]) : data.queue;

  return (
    <CrmShell title="Today">
      <OutcomeSheet open={!!target} onOpenChange={(o) => !o && setTarget(null)} target={target} onSaved={load} />
      {isStandalone() && <PushToggle compact />}
      <div className="grid grid-cols-4 gap-2 mb-4" data-testid="today-counters">
        {COUNTERS.map(([k, l, Icon, tone]) => (
          <button key={k} onClick={() => setFilter(filter === k ? "" : k)} data-testid={`today-counter-${k}`}
            className={`text-left rounded-xl border p-2.5 sm:p-4 transition-colors ${filter === k ? "border-brass bg-brass/10" : "border-white/10 bg-[#0F2E23]"}`}>
            <Icon className={`w-4 h-4 ${tone}`} />
            <div className="text-xl sm:text-2xl font-semibold text-white mt-1" data-testid={`today-count-${k}`}>{data.counts[k]}</div>
            <div className="text-[10px] sm:text-xs text-emerald-100/60 leading-tight">{l}</div>
          </button>
        ))}
      </div>
      {data.failed_emails > 0 && (
        <Link to="/crm/leads" className="flex items-center gap-2 text-xs text-red-200 bg-red-950/40 border border-red-500/30 rounded-lg px-3 py-2 mb-4" data-testid="today-failed-emails">
          <MailWarning className="w-4 h-4" />{data.failed_emails} email{data.failed_emails > 1 ? "s" : ""} not sent — review <ChevronRight className="w-3.5 h-3.5 ml-auto" />
        </Link>
      )}
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="text-sm font-semibold text-white">Who to contact{filter ? ` · ${COUNTERS.find((c) => c[0] === filter)[1]}` : ""}</h2>
        <span className="text-xs text-emerald-100/40" data-testid="today-queue-total">{queue.length} item{queue.length === 1 ? "" : "s"}</span>
      </div>
      {queue.length === 0
        ? <div className="text-center text-emerald-100/50 text-sm py-10 bg-[#0F2E23] rounded-xl border border-white/10 mb-6" data-testid="today-empty">All clear — nothing waiting.</div>
        : <ul className="space-y-2.5 mb-6" data-testid="today-queue">{queue.map((it) => <QueueRow key={`${it.kind}-${it.store}-${it.id}-${it.referral_id || ""}`} it={it} onLog={setTarget} onBump={bump} />)}</ul>}
      <AuctionStrip a={data.auctions} />
    </CrmShell>
  );
}
